import { UserRole } from '@/components/authnz/types/rbac';
import type { Subscription } from '@/components/subscriptions/types/subscription';
import { isPurchaseHandoffPath } from '@/lib/purchase-handoff-path';

export type AccessState = 'ok' | 'invited-waiting' | 'payment-pending';

export type StoredAccessStatus = 'ACTIVE' | 'INACTIVE' | 'BANNED' | null;

const APP_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMINISTRATOR,
  UserRole.PRACTITIONER_ADMINISTRATOR,
  UserRole.PRACTITIONER,
  UserRole.STUDENT,
  UserRole.TUTOR,
  UserRole.SALES,
];

export { APP_ACCESS_ROLES };

export function userHasAppAccess(roles: UserRole[] | string[] | undefined | null): boolean {
  if (!roles?.length) return false;
  return roles.some((role) => APP_ACCESS_ROLES.includes(role as UserRole));
}

/** A session whose only role is client. Practitioner shells are not a destination for it. */
export function isClientsOnlySession(roles: UserRole[] | string[] | undefined | null): boolean {
  const list = roles ?? [];
  return list.length > 0 && list.every((role) => role === UserRole.CLIENT);
}

const PRACTITIONER_HOME_ROLES: UserRole[] = [
  UserRole.ADMINISTRATOR,
  UserRole.PRACTITIONER_ADMINISTRATOR,
  UserRole.PRACTITIONER,
  UserRole.STUDENT,
  UserRole.TUTOR,
];

/** Sales-only users land on Prospects; everyone else with app access keeps /dashboard. */
export function resolvePostLoginPath(
  roles: UserRole[] | string[] | undefined | null,
  returnTo?: string | null,
): string {
  if (returnTo && returnTo.startsWith('/')) {
    return returnTo;
  }
  const list = (roles ?? []) as UserRole[];
  const hasSales = list.includes(UserRole.SALES);
  const hasPractitionerHome = list.some((role) => PRACTITIONER_HOME_ROLES.includes(role));
  if (hasSales && !hasPractitionerHome) {
    return '/sales/prospects';
  }
  return '/dashboard';
}

function isBrowsePlansWelcomePath(pathOrUrl: string): boolean {
  try {
    const url = pathOrUrl.startsWith('http') ? new URL(pathOrUrl) : null;
    const pathname = url ? url.pathname : pathOrUrl.split('?')[0];
    const search = url ? url.search : `?${pathOrUrl.split('?')[1] ?? ''}`;
    return pathname === '/welcome' && new URLSearchParams(search).get('intent') === 'browse-plans';
  } catch {
    return false;
  }
}

/** Safe without-app-access destinations: a purchase in progress, or browsing plans. */
function isSafeUnlicensedReturnTo(returnTo: string | null | undefined): returnTo is string {
  if (!returnTo || !returnTo.startsWith('/')) return false;
  return isPurchaseHandoffPath(returnTo) || isBrowsePlansWelcomePath(returnTo);
}

/**
 * Where /login sends the browser once the server session is ready (#7782).
 * With app access, defer to resolvePostLoginPath. Without it, only a
 * whitelisted purchase-flow destination is honoured — never an arbitrary
 * returnTo, which would be an open redirect for a signed-in-but-roleless
 * session.
 */
export function resolveLoginDestination(params: {
  hasAppAccess: boolean;
  roles: UserRole[] | string[] | undefined | null;
  returnTo?: string | null;
}): string {
  if (params.hasAppAccess) {
    return resolvePostLoginPath(params.roles, params.returnTo);
  }
  if (isSafeUnlicensedReturnTo(params.returnTo)) {
    return params.returnTo;
  }
  return '/signup-required';
}

function isActiveSubscriptionStatus(status: Subscription['status']): boolean {
  return status === 'active' || status === 'trialing';
}

function hasActiveSubscriptionMirror(subscriptions: Subscription[]): boolean {
  return subscriptions.some((subscription) => isActiveSubscriptionStatus(subscription.status));
}

/**
 * Derive session bootstrap accessState for /signup-required UX.
 */
export function resolveAccessState(params: {
  roles: UserRole[];
  subscriptions: Subscription[];
  accessStatus?: StoredAccessStatus;
}): AccessState {
  const hasAppAccess = userHasAppAccess(params.roles);
  if (hasAppAccess) {
    return 'ok';
  }

  if (hasActiveSubscriptionMirror(params.subscriptions)) {
    return 'payment-pending';
  }

  return 'invited-waiting';
}
